import { useState, useEffect, useRef } from "react";
import { productPriceApi, productApi } from "../api/api";
import { emitDataMutation } from "../utils/appEvents";
import MasterDetailView from "../components/MasterDetailView";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";


/* ── Toast ── */
function Toast({ message, type, onClose }) {
    useEffect(() => {
        const t = setTimeout(onClose, 3200);
        return () => clearTimeout(t);
    }, [onClose]);
    return (
        <div className={`rv-toast rv-toast--${type}`}>
            <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
            <span className="rv-toast-msg">{message}</span>
            <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
        </div>
    );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
    return (
        <div className="rv-dialog-backdrop" style={{ zIndex: 500 }}>
            <div className="rv-dialog">
                <div className="rv-dialog-icon"><TrashIcon /></div>
                <h3 className="rv-dialog-title">Delete Price Entry?</h3>
                <p className="rv-dialog-desc">
                    <strong>{name}</strong> will be permanently removed. This action cannot be undone.
                </p>
                <div className="rv-dialog-actions">
                    <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
                    <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
                </div>
            </div>
        </div>
    );
}

/* ── Product Price History Master-Detail View ── */
function PriceHistoryView({ productName, product, entries, onBack, onEdit, onDelete, onNewPrice }) {
    const todayISO = new Date().toISOString().slice(0, 10);
    const sorted = [...entries].sort((a, b) => new Date(b.effdt) - new Date(a.effdt));
    const activePrice = sorted.find(e => e.effdt && e.effdt.slice(0, 10) <= todayISO);
    const [selected, setSelected] = useState(activePrice || sorted[0] || null);

    useEffect(() => {
        if (sorted.length > 0 && (!selected || !sorted.find(e => e.priceID === selected.priceID))) {
            setSelected(activePrice || sorted[0]);
        }
    }, [entries, activePrice]);

    const fmt = (d) => {
        if (!d) return "—";
        return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    };
    const fmtINR = (v) =>
        v != null ? `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";

    const isCurrent = (entry) => activePrice?.priceID === entry?.priceID;

    return (
        <MasterDetailView
            title={`Price History — ${productName}`}
            subtitle={product?.productCode ? `Product Code: ${product.productCode}` : "Inspect pricing evolution, margin changes, and historical price records."}
            backLabel="Back to Product Pricing"
            onBack={onBack}
            badgeText={`${sorted.length} price ${sorted.length === 1 ? "entry" : "entries"}`}
            timelineTitle="Price Timeline"
            items={sorted}
            selectedItem={selected}
            onSelectItem={item => setSelected(item)}
            emptyTitle="No Price Entries Found"
            emptyMessage="No price records found for this product."
            extraHeaderActions={
                onNewPrice && (
                    <button className="rv-btn rv-btn--primary" onClick={onNewPrice}>
                        <PlusIcon /> New Price Entry
                    </button>
                )
            }
            renderTimelineItem={(entry, idx, isSelected) => {
                const cur = isCurrent(entry);
                return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: isSelected ? "var(--taupe-dark)" : "var(--dark)" }}>
                                <CalIcon />
                                {fmt(entry.effdt)}
                            </div>
                            {cur ? (
                                <span style={{
                                    fontSize: 9.5, fontWeight: 700, color: "#27ae60",
                                    background: "rgba(39,174,96,.12)", padding: "2px 7px", borderRadius: 10,
                                    textTransform: "uppercase", letterSpacing: ".4px",
                                }}>
                                    Current
                                </span>
                            ) : (entry.effdt && entry.effdt.slice(0, 10) > todayISO) ? (
                                <span style={{
                                    fontSize: 9.5, fontWeight: 700, color: "#d97706",
                                    background: "rgba(217,119,6,.12)", padding: "2px 7px", borderRadius: 10,
                                    textTransform: "uppercase", letterSpacing: ".4px",
                                }}>
                                    Upcoming
                                </span>
                            ) : (
                                <span style={{
                                    fontSize: 9.5, fontWeight: 600, color: "var(--taupe)",
                                    background: "rgba(185,167,146,.12)", padding: "2px 6px", borderRadius: 10,
                                }}>
                                    Archived
                                </span>
                            )}
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                            <span style={{ fontSize: 13.5, fontWeight: 800, color: "var(--dark)" }}>
                                {fmtINR(entry.grossSellingPrice)}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--taupe)" }}>
                                Margin: {entry.marginPercent}%
                            </span>
                        </div>

                        <div style={{ fontSize: 11, color: "var(--taupe-dark)", display: "flex", gap: 10 }}>
                            <span>Landing: {fmtINR(entry.landingCost)}</span>
                            <span>Selling: {fmtINR(entry.sellingPrice)}</span>
                        </div>
                    </div>
                );
            }}
            renderDetail={(entry) => {
                const cur = isCurrent(entry);
                const marginAmt = (Number(entry.sellingPrice) || 0) - (Number(entry.landingCost) || 0);

                return (
                    <>
                        {/* Header */}
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            borderBottom: "1px solid rgba(185,167,146,.25)",
                            paddingBottom: 16,
                            flexWrap: "wrap",
                            gap: 12,
                        }}>
                            <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                                    <h3 style={{ fontSize: 18, fontWeight: 700, color: "var(--dark)", margin: 0 }}>
                                        {fmtINR(entry.grossSellingPrice)}
                                    </h3>
                                    {cur ? (
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, color: "#27ae60",
                                            background: "rgba(39,174,96,.12)", padding: "3px 10px", borderRadius: 20,
                                        }}>
                                            Active Current Price
                                        </span>
                                    ) : (
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, color: "var(--taupe)",
                                            background: "rgba(185,167,146,.15)", padding: "3px 10px", borderRadius: 20,
                                        }}>
                                            Historical Price Record
                                        </span>
                                    )}
                                </div>
                                <p style={{ margin: 0, fontSize: 12.5, color: "var(--taupe)" }}>
                                    Effective from: <strong>{fmt(entry.effdt)}</strong> • Product: <strong>{productName}</strong>
                                </p>
                            </div>

                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button
                                    className="rv-btn rv-btn--ghost"
                                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                                    onClick={() => onEdit(entry)}
                                >
                                    <EditIcon /> Edit Price
                                </button>
                                <button
                                    className="rv-btn rv-btn--ghost"
                                    style={{ display: "flex", alignItems: "center", gap: 6, color: "#c0392b", borderColor: "rgba(192,57,43,.3)" }}
                                    onClick={() => onDelete(entry)}
                                >
                                    <TrashIcon /> Delete
                                </button>
                            </div>
                        </div>

                        {/* Pricing Breakdown Grid */}
                        <div>
                            <label className="rv-label" style={{ marginBottom: 12, fontSize: 12 }}>
                                Price Calculation & Cost Breakdown
                            </label>

                            <div className="rv-price-breakdown-grid">
                                <div style={{ background: "#FAF7F4", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--taupe)", textTransform: "uppercase" }}>Landing Cost</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: "var(--dark)", marginTop: 4 }}>{fmtINR(entry.landingCost)}</div>
                                    <div style={{ fontSize: 10.5, color: "var(--taupe)", marginTop: 2 }}>Base procurement cost</div>
                                </div>

                                <div style={{ background: "#FAF7F4", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--taupe)", textTransform: "uppercase" }}>Margin Percentage</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: "#27ae60", marginTop: 4 }}>{entry.marginPercent}%</div>
                                    <div style={{ fontSize: 10.5, color: "var(--taupe)", marginTop: 2 }}>Profit markup ({fmtINR(marginAmt)})</div>
                                </div>

                                <div style={{ background: "#FAF7F4", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--taupe)", textTransform: "uppercase" }}>Base Selling Price</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: "var(--dark)", marginTop: 4 }}>{fmtINR(entry.sellingPrice)}</div>
                                    <div style={{ fontSize: 10.5, color: "var(--taupe)", marginTop: 2 }}>Landing + Margin</div>
                                </div>

                                <div style={{ background: "#FAF7F4", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--taupe)", textTransform: "uppercase" }}>Other / Making Charges</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: "var(--dark)", marginTop: 4 }}>{fmtINR(entry.otherCharges)}</div>
                                    <div style={{ fontSize: 10.5, color: "var(--taupe)", marginTop: 2 }}>Additional fees</div>
                                </div>

                                <div style={{ background: "#FAF4EF", border: "1.5px solid var(--taupe)", borderRadius: 10, padding: 14, gridColumn: "1 / -1" }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--taupe-dark)", textTransform: "uppercase" }}>Final Gross Selling Price</div>
                                    <div style={{ fontSize: 22, fontWeight: 800, color: "var(--dark)", marginTop: 4 }}>{fmtINR(entry.grossSellingPrice)}</div>
                                    <div style={{ fontSize: 11, color: "var(--taupe)", marginTop: 2 }}>Customer invoice selling price inclusive of charges</div>
                                </div>
                            </div>
                        </div>
                    </>
                );
            }}
        />
    );
}

/* ── Price Form Modal ── */
function PriceModal({ initial, products, onSave, onClose, saving }) {
    const isEdit = !!initial?.priceID;
    const todayISO = new Date().toISOString().split("T")[0];

    const [form, setForm] = useState({
        productID:         initial?.productID         != null ? String(initial.productID)         : "",
        effdt:             initial?.effdt              ? initial.effdt.split("T")[0]               : todayISO,
        landingCost:       initial?.landingCost        != null ? String(initial.landingCost)        : "",
        marginPercent:     initial?.marginPercent      != null ? String(initial.marginPercent)      : "",
        sellingPrice:      initial?.sellingPrice       != null ? String(initial.sellingPrice)       : "",
        otherCharges:      initial?.otherCharges       != null ? String(initial.otherCharges)       : "0",
        grossSellingPrice: initial?.grossSellingPrice  != null ? String(initial.grossSellingPrice)  : "",
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const landing = parseFloat(form.landingCost)   || 0;
        const margin  = parseFloat(form.marginPercent) || 0;
        const other   = parseFloat(form.otherCharges)  || 0;
        const selling = landing * (1 + margin / 100);
        const gross   = selling + other;
        setForm(f => ({
            ...f,
            sellingPrice:      selling > 0 ? selling.toFixed(2) : f.sellingPrice,
            grossSellingPrice: gross   > 0 ? gross.toFixed(2)   : f.grossSellingPrice,
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.landingCost, form.marginPercent, form.otherCharges]);

    const validate = () => {
        const e = {};
        if (!form.productID)               e.productID        = "Product is required.";
        if (!form.effdt)                   e.effdt            = "Effective date is required.";
        if (form.landingCost === "")       e.landingCost      = "Landing cost is required.";
        else if (isNaN(Number(form.landingCost)) || Number(form.landingCost) < 0)
                                           e.landingCost      = "Enter a valid amount.";
        if (form.marginPercent === "")     e.marginPercent    = "Margin % is required.";
        else if (isNaN(Number(form.marginPercent)) || Number(form.marginPercent) < 0)
                                           e.marginPercent    = "Enter a valid percentage.";
        if (form.sellingPrice === "")      e.sellingPrice     = "Selling price is required.";
        if (form.grossSellingPrice === "") e.grossSellingPrice = "Gross selling price is required.";
        return e;
    };

    const handleSubmit = () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        onSave({
            ...(isEdit ? { priceID: initial.priceID } : {}),
            productID:         Number(form.productID),
            effdt:             form.effdt,
            landingCost:       Number(form.landingCost),
            marginPercent:     Number(form.marginPercent),
            sellingPrice:      Number(form.sellingPrice),
            otherCharges:      Number(form.otherCharges) || 0,
            grossSellingPrice: Number(form.grossSellingPrice),
        });
    };

    const set = (key, val) => {
        setForm(f => ({ ...f, [key]: val }));
        setErrors(v => ({ ...v, [key]: "" }));
    };

    const numField = (key, label, required = true) => (
        <div className="rv-field">
            <label className="rv-label">
                {label} {required && <span className="rv-required">*</span>}
            </label>
            <input
                className={`rv-input ${errors[key] ? "rv-input--err" : ""}`}
                type="number" min="0" step="0.01"
                value={form[key]}
                onChange={e => set(key, e.target.value)}
            />
            {errors[key] && <p className="rv-field-err">{errors[key]}</p>}
        </div>
    );

    return (
        <div className="rv-dialog-backdrop">
            <div className="rv-dialog rv-dialog--form">
                <div className="rv-modal-header">
                    <div className="rv-modal-icon"><TagIcon /></div>
                    <div>
                        <h3 className="rv-modal-title">{isEdit ? "Edit Price Entry" : "New Price Entry"}</h3>
                        <p className="rv-modal-sub">
                            {isEdit ? "Update the price details below." : "Add a new effective-date price entry for a product."}
                        </p>
                    </div>
                    <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
                </div>

                <div className="rv-modal-body">
                    <div className="rv-field">
                        <label className="rv-label">Product <span className="rv-required">*</span></label>
                        <SearchableDropdown
                            options={products.map(p => ({
                                ...p,
                                label: p.productCode ? `${p.productName} (${p.productCode})` : p.productName
                            }))}
                            valueKey="productID"
                            labelKey="label"
                            value={form.productID}
                            onChange={val => set("productID", val)}
                            placeholder="— Select product —"
                            searchPlaceholder="Search products…"
                            disabled={isEdit}
                            error={errors.productID}
                            pageSize={10}
                        />
                    </div>

                    <div className="rv-field">
                        <label className="rv-label">
                            Effective Date {!isEdit && <span className="rv-required">*</span>}
                        </label>
                        {isEdit ? (
                            <>
                                <p className="rv-field-hint rv-effdt-hint"><CalIcon /> Effective date cannot be changed on edit</p>
                                <input className="rv-input rv-input--date" type="date" value={form.effdt} disabled style={{ opacity: 0.5, cursor: "not-allowed" }} />
                            </>
                        ) : (
                            <>
                                <input
                                    className={`rv-input rv-input--date ${errors.effdt ? "rv-input--err" : ""}`}
                                    type="date" value={form.effdt}
                                    onChange={e => set("effdt", e.target.value)}
                                />
                                {errors.effdt && <p className="rv-field-err">{errors.effdt}</p>}
                            </>
                        )}
                    </div>

                    <div className="rv-field-row-2col">
                        {numField("landingCost",   "Landing Cost (₹)")}
                        {numField("marginPercent", "Margin %")}
                    </div>
                    <div className="rv-field-row-2col">
                        {numField("sellingPrice", "Selling Price (₹)")}
                        {numField("otherCharges", "Other Charges (₹)", false)}
                    </div>
                    <div className="rv-field">
                        <label className="rv-label">Gross Selling Price (₹) <span className="rv-required">*</span></label>
                        <input
                            className={`rv-input ${errors.grossSellingPrice ? "rv-input--err" : ""}`}
                            type="number" min="0" step="0.01"
                            value={form.grossSellingPrice}
                            onChange={e => set("grossSellingPrice", e.target.value)}
                            style={{ fontWeight: 600 }}
                        />
                        {errors.grossSellingPrice && <p className="rv-field-err">{errors.grossSellingPrice}</p>}
                        <p className="rv-field-hint">Auto-calculated as Selling Price + Other Charges</p>
                    </div>
                </div>

                <div className="rv-modal-footer">
                    <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
                        {saving ? <span className="rv-spinner" /> : null}
                        {isEdit ? "Update" : "Create"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ══════════════════════════════════════
   MAIN PRODUCT PRICE PAGE
══════════════════════════════════════ */
export default function ProductPrice() {
    const [records,  setRecords]  = useState([]);
    const [products, setProducts] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [search,   setSearch]   = useState("");

    const [modal,   setModal]   = useState(null);
    const [saving,  setSaving]  = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [toast,   setToast]   = useState(null);
    const [drawer,  setDrawer]  = useState(null); // { productName, entries }

    const tableRef = useRef(null);
    const [tableReady, setTableReady] = useState(false);
    // Columns: #, Product, Latest Date, Latest Gross Price, Entries, Actions
    useResizableColumns(tableRef, tableReady, [50, 220, 140, 160, 100, 120]);

    const fetchAll = async () => {
        try {
            setLoading(true); setTableReady(false);
            const [priceData, productData] = await Promise.all([
                productPriceApi.getAll(),
                productApi.getAll(),
            ]);
            setRecords(Array.isArray(priceData)   ? priceData   : priceData?.data   ?? []);
            setProducts(Array.isArray(productData) ? productData : productData?.data ?? []);
        } catch (err) {
            showToast(err.message || "Failed to load data.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);
    useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

    const showToast = (message, type = "success") => setToast({ message, type });
    const productMap = Object.fromEntries(products.map(p => [p.productID, p]));

    /* ── One row per product showing latest active price ── */
    const latestByProduct = (() => {
        const todayISO = new Date().toISOString().slice(0, 10);
        const map = new Map();
        records.forEach(r => {
            const isEffective = r.effdt && r.effdt.slice(0, 10) <= todayISO;
            if (isEffective) {
                const existing = map.get(r.productID);
                if (!existing || new Date(r.effdt) > new Date(existing.effdt)) {
                    map.set(r.productID, r);
                }
            }
        });
        return Array.from(map.values()).sort((a, b) => {
            const na = productMap[a.productID]?.productName ?? "";
            const nb = productMap[b.productID]?.productName ?? "";
            return na.localeCompare(nb);
        });
    })();

    const entriesForProduct = (productID) =>
        records.filter(r => r.productID === productID);

    const filtered = latestByProduct.filter(r => {
        const prod = productMap[r.productID];
        const name = prod?.productName?.toLowerCase() ?? "";
        const code = prod?.productCode?.toLowerCase() ?? "";
        const q    = search.toLowerCase();
        return name.includes(q) || code.includes(q);
    });

    const { paged, page, totalPages, pageSize, setPage, setPageSize, pageStart, pageEnd, totalItems } =
        usePagination(filtered, 10);

    /* ── Save ── */
    const handleSave = async (formData) => {
        setSaving(true);
        try {
            if (formData.priceID) {
                await productPriceApi.update(formData);
                
                showToast("Price entry updated successfully.");
            } else {
                await productPriceApi.create(formData);
                showToast("Price entry created successfully.");
            }
            setModal(null);
            emitDataMutation("price");
            fetchAll();
        } catch (err) {
            const msg = err.message || "";
            if (msg.toLowerCase().includes("already exists") || msg.toLowerCase().includes("effdt")) {
                showToast("A price entry already exists for this product on that date. Choose a different effective date.", "error");
            } else {
                showToast(msg || "Something went wrong.", "error");
            }
        } finally {
            setSaving(false);
        }
    };

    /* ── Delete single entry by priceID ── */
    const handleDelete = async () => {
        if (!confirm) return;
        try {
            await productPriceApi.remove(confirm.id);
            showToast("Price entry deleted.");
            const deletedId = confirm.id;
            setConfirm(null);
            setDrawer(prev => {
                if (!prev) return null;
                const updated = prev.entries.filter(e => e.priceID !== deletedId);
                return updated.length > 0 ? { ...prev, entries: updated } : null;
            });
            emitDataMutation("price");
            fetchAll();
        } catch (err) {
            console.log("🚀 ~ handleDelete ~ err:", err)
            
            showToast(err.message || "Delete failed.", "error");
        }
    };

    const fmt = (d) => {
        if (!d) return "—";
        return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    };
    const fmtINR = (v) =>
        v != null ? `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";

    if (drawer) {
        const prod = products.find(p => p.productName === drawer.productName || (drawer.entries[0] && p.productID === drawer.entries[0].productID));
        const currentEntries = prod ? entriesForProduct(prod.productID) : drawer.entries;
        return (
            <div className="rv-tax-page">
                {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
                {modal && (
                    <PriceModal
                        initial={modal.data}
                        products={products}
                        onSave={handleSave}
                        onClose={() => setModal(null)}
                        saving={saving}
                    />
                )}
                {confirm && (
                    <ConfirmDialog
                        name={confirm.name}
                        onConfirm={handleDelete}
                        onCancel={() => setConfirm(null)}
                    />
                )}
                <PriceHistoryView
                    productName={drawer.productName}
                    product={prod}
                    entries={currentEntries}
                    onBack={() => setDrawer(null)}
                    onEdit={entry => setModal({ data: entry })}
                    onDelete={entry => setConfirm({
                        id:   entry.priceID,
                        name: `${drawer.productName} — ${fmt(entry.effdt)}`,
                    })}
                    onNewPrice={() => setModal({ data: { productID: prod?.productID } })}
                />
            </div>
        );
    }

    return (
        <div className="rv-tax-page">

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
            {modal && (
                <PriceModal
                    initial={modal.data}
                    products={products}
                    onSave={handleSave}
                    onClose={() => setModal(null)}
                    saving={saving}
                />
            )}
            {confirm && (
                <ConfirmDialog
                    name={confirm.name}
                    onConfirm={handleDelete}
                    onCancel={() => setConfirm(null)}
                />
            )}

            {/* Topbar */}
            <div className="rv-tax-topbar">
                <div className="rv-tax-topbar-left">
                    <h2 className="rv-tax-heading">Product Pricing</h2>
                    <span className="rv-tax-count">{latestByProduct.length} products · {records.length} entries</span>
                </div>
                <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                    <PlusIcon /> New Price Entry
                </button>
            </div>

            {/* Filters */}
            <div className="rv-tax-filters">
                <div className="rv-tax-search">
                    <SearchIcon />
                    <input
                        placeholder="Search by product name or code…"
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }}
                    />
                    {search && (
                        <button className="rv-tax-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
                            <CloseIcon />
                        </button>
                    )}
                </div>
            </div>

            {/* Table / Cards container */}
            <div className="rv-card rv-tax-card">
                {/* Desktop Table View */}
                <div className="rv-desktop-table-view">
                    {loading ? (
                        <div className="rv-tax-empty">
                            <div className="rv-loader" /><p>Loading price data…</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="rv-tax-empty">
                            <div className="rv-empty-icon"><TagIcon /></div>
                            <p className="rv-empty-title">No price entries found</p>
                            <p className="rv-empty-sub">{search ? "Try a different search term." : "Create the first price entry to get started."}</p>
                            {!search && (
                                <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                                    <PlusIcon /> New Price Entry
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="rv-table-wrap rv-tax-table-wrap">
                            <table className="rv-table rv-tax-table" ref={tableRef}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Product</th>
                                        <th>Latest Eff. Date</th>
                                        <th>Latest Gross Price</th>
                                        <th>Entries</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paged.map((r, i) => {
                                        const prod    = productMap[r.productID];
                                        const name    = prod?.productName ?? `Product #${r.productID}`;
                                        const count   = entriesForProduct(r.productID).length;
                                        return (
                                            <tr
                                                key={r.productID}
                                                className="rv-tax-row"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => setDrawer({
                                                    productName: name,
                                                    entries: entriesForProduct(r.productID),
                                                })}
                                            >
                                                <td className="rv-tax-idx">{pageStart + i}</td>
                                                <td>
                                                    <div className="rv-tax-name-cell">
                                                        <span className="rv-tax-name">{name}</span>
                                                        {prod?.productCode && (
                                                            <span style={{ fontSize: 11, color: "var(--taupe)", marginLeft: 4 }}>
                                                                {prod.productCode}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="rv-effdt-cell">
                                                        <span className="rv-effdt-icon"><CalIcon /></span>
                                                        <span className="rv-effdt-val">{fmt(r.effdt)}</span>
                                                    </div>
                                                </td>
                                                <td><strong>{fmtINR(r.grossSellingPrice)}</strong></td>
                                                <td>
                                                    <span className="rv-history-chip" style={{ cursor: "default" }}>
                                                        <HistoryIcon /> {count} {count === 1 ? "entry" : "entries"}
                                                    </span>
                                                </td>
                                                <td onClick={e => e.stopPropagation()}>
                                                    <div className="rv-actions">
                                                        <button
                                                            className="rv-action-btn rv-action-btn--edit"
                                                            title="Add new price entry for this product"
                                                            onClick={() => setModal({ data: { productID: r.productID } })}
                                                        >
                                                            <PlusIcon />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Mobile Cards View */}
                <div className="rv-mobile-cards-view">
                    <RvDataCardList
                        loading={loading}
                        items={paged}
                        emptyIcon={<TagIcon />}
                        emptyTitle="No price entries found"
                        emptySubtitle={search ? "Try a different search term." : "Create the first price entry to get started."}
                        emptyAction={
                            !search && (
                                <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                                    <PlusIcon /> New Price Entry
                                </button>
                            )
                        }
                        renderCard={(r, i) => {
                            const prod  = productMap[r.productID];
                            const name  = prod?.productName ?? `Product #${r.productID}`;
                            const count = entriesForProduct(r.productID).length;
                            return (
                                <RvDataCard
                                    key={r.productID}
                                    index={pageStart + i}
                                    title={name}
                                    subtitle={prod?.productCode || `ID #${r.productID}`}
                                    badge={
                                        <span className="rv-history-chip" style={{ cursor: "default" }}>
                                            <HistoryIcon /> {count} {count === 1 ? "entry" : "entries"}
                                        </span>
                                    }
                                    fields={[
                                        { label: "Effective Date", value: fmt(r.effdt) },
                                        { label: "Gross Selling Price", value: <strong>{fmtINR(r.grossSellingPrice)}</strong> },
                                        { label: "Base Selling",   value: r.sellingPrice ? fmtINR(r.sellingPrice) : "—" },
                                        { label: "Margin",         value: r.marginPercent != null ? `${r.marginPercent}%` : "—" },
                                    ]}
                                    onClick={() => setDrawer({
                                        productName: name,
                                        entries: entriesForProduct(r.productID),
                                    })}
                                    actions={
                                        <div className="rv-actions" onClick={e => e.stopPropagation()}>
                                            <button
                                                className="rv-action-btn rv-action-btn--edit"
                                                title="Add new price entry for this product"
                                                onClick={() => setModal({ data: { productID: r.productID } })}
                                            >
                                                <PlusIcon />
                                            </button>
                                        </div>
                                    }
                                />
                            );
                        }}
                    />
                </div>

                {/* Shared Pagination */}
                {!loading && filtered.length > 0 && (
                    <Pagination
                        page={page} totalPages={totalPages} pageSize={pageSize}
                        setPage={setPage} setPageSize={setPageSize}
                        pageStart={pageStart} pageEnd={pageEnd} totalItems={totalItems}
                    />
                )}
            </div>

            {/* Resize hint (Desktop Only) */}
            {!loading && filtered.length > 0 && (
                <p className="rv-resize-hint rv-desktop-table-view">
                    <GripIcon /> Drag column borders to resize · Click a row to view all price entries
                </p>
            )}
        </div>
    );
}

/* ── Icons ── */
function PlusIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function EditIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function TrashIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>; }
function CloseIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function TagIcon()     { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="18" height="18"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function HistoryIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="12" height="12"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/><polyline points="12 7 12 12 15 15"/></svg>; }
function CalIcon()     { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 12, height: 12, display: "inline", verticalAlign: "middle" }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>; }
function GripIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>; }